import type {
  CheckoutModuleApi,
  SdkConfig,
  SdkModuleContext,
  SdkPageContext,
} from '@ekhodealer/ekho-common';

import { SdkApiClient } from '../../config/server/apiClient';

import {
  attachAttributionToUrl,
  type CheckoutClientConfig,
  createBuildToOrderCart,
  createCart,
  createInventoryCart,
  isAvailable,
  redirect,
  validateBuildToOrderCart,
  validateInventoryCart,
} from './client';
import { installLinkDecoration } from './linkDecoration';

export interface CheckoutCapabilityResult {
  api: CheckoutModuleApi;
  cleanup: () => void;
}

/**
 * Built-in capability — checkout is a thin BFF wrapper (cart create / availability /
 * redirect / link decoration), not a UI feature, so it ships inside the loader
 * bundle alongside page-context rather than as a separately-loaded module. Per
 * `apps/ekho-sdk/src/capabilities/README.md`: capabilities are always present and
 * activate based on config.
 *
 * Returns `null` when checkout is absent/disabled in config; the loader leaves
 * `window.Ekho.checkout` undefined in that case so callers can feature-detect.
 * When enabled, click-time decoration always rewrites Ekho storefront anchors.
 */
export function initCheckoutCapability(
  config: SdkConfig,
  context: SdkModuleContext,
): CheckoutCapabilityResult | null {
  const moduleConfig = config.modules.checkout;
  if (!moduleConfig?.enabled) return null;

  const clientConfig: CheckoutClientConfig = {
    svpId: config.svpId,
    client: new SdkApiClient(context.apiBaseUrl),
    attribution: context.attribution,
    reportedAvailability: new Set(),
  };

  // `page:context` also fires on title mutations, so only a URL change counts
  // as a new page for availability dedupe.
  let pageUrl = window.location.href;
  const onPageContext = (payload: unknown): void => {
    const { url } = payload as SdkPageContext;
    if (url === pageUrl) return;
    pageUrl = url;
    clientConfig.reportedAvailability.clear();
  };
  context.bus.on('page:context', onPageContext);
  const stopLinkDecoration = installLinkDecoration({ attribution: context.attribution });

  return {
    api: {
      createInventoryCart: (options) => createInventoryCart(options, clientConfig),
      createBuildToOrderCart: (options) => createBuildToOrderCart(options, clientConfig),
      validateInventoryCart: (options) => validateInventoryCart(options, clientConfig),
      validateBuildToOrderCart: (options) => validateBuildToOrderCart(options, clientConfig),
      createCart: (options) => createCart(options, clientConfig),
      isAvailable: (options) => isAvailable(options, clientConfig),
      redirect,
      attachAttributionToUrl: (url) => attachAttributionToUrl(url, clientConfig),
    },
    cleanup: () => {
      context.bus.off('page:context', onPageContext);
      stopLinkDecoration();
    },
  };
}
